import React, { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatError } from "@/lib/api";
import { toast } from "sonner";
import { LockKey } from "@phosphor-icons/react";

/**
 * Renders a full-screen modal that blocks the app until the user changes
 * their initial temporary password. Fires automatically whenever
 * `auth/me` reports `must_change_password: true`.
 */
export default function ChangePasswordGate({ children }) {
  const { user, setUser } = useAuth();
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  if (!user || !user.must_change_password) return children;

  async function submit(e) {
    e.preventDefault();
    if (pw.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    if (pw !== confirm) { toast.error("Passwords do not match"); return; }
    setBusy(true);
    try {
      await api.patch(`/users/${user.id}`, { password: pw });
      // Clear the flag by refetching /auth/me
      const { data } = await api.get("/auth/me");
      setUser(data);
      toast.success("Password updated successfully");
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-[#09090b]/95 flex items-center justify-center p-4" data-testid="change-password-gate">
      <div className="bg-white w-full max-w-md p-8">
        <div className="flex items-center gap-3 mb-4 pb-4 border-b border-[#e4e4e7]">
          <LockKey size={24} weight="bold" className="text-[#002FA7]" />
          <div>
            <h2 className="font-heading text-xl font-black">Set a new password</h2>
            <p className="text-xs text-[#71717a]">Your account was created with a temporary password. Please choose a permanent one to continue.</p>
          </div>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <label className="block">
            <div className="phizo-label text-[10px] mb-1">New password</div>
            <input
              data-testid="new-pw-input"
              type="password"
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              className="w-full border border-[#e4e4e7] bg-white px-3 py-2 text-sm focus:border-[#002FA7] focus:ring-2 focus:ring-[#002FA7]/20 outline-none"
              placeholder="Min 6 characters"
              autoFocus
            />
          </label>
          <label className="block">
            <div className="phizo-label text-[10px] mb-1">Confirm password</div>
            <input
              data-testid="confirm-pw-input"
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="w-full border border-[#e4e4e7] bg-white px-3 py-2 text-sm focus:border-[#002FA7] focus:ring-2 focus:ring-[#002FA7]/20 outline-none"
            />
          </label>
          <button
            data-testid="save-new-pw-btn"
            type="submit"
            disabled={busy}
            className="w-full bg-[#002FA7] text-white py-2.5 font-medium hover:bg-[#001f7a] disabled:opacity-60"
          >
            {busy ? "Saving…" : "Save password & continue"}
          </button>
        </form>
      </div>
    </div>
  );
}
